import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  IconButton,
  Icon,
  TextField,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';

import { sortArrByKey, convertObjToArr } from '../../../../../utils';
import ActionsCourse from '../../../../../store/ducks/course';
import useStyles from './styles';

const WizardQuestions = props => {
  const classes = useStyles();
  const { open, handleClose, questions } = props;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { course: courseId, lesson: lessonId } = useSelector(
    state => state.course.current,
  );
  const { questionsUpdating } = useSelector(
    state => state.course.list[courseId].lessons[lessonId],
  );
  const [ammount, setAmmount] = useState(0);
  const [activeTab, setActiveTab] = useState(1);
  const [values, setValues] = useState({});
  const [questionsList, setQuestionsList] = useState([]);
  const [sendingAnswers, setSendingAnswers] = useState(false);
  const slider = React.useRef(null);

  const settings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      slidesToScroll: 1,
      variableWidth: false,
      arrows: false,
      draggable: false,
      beforeChange: (current, next) => setActiveTab(next + 1),
    }),
    [],
  );

  useEffect(() => {
    if (!questionsUpdating && sendingAnswers) {
      setSendingAnswers(false);
      handleClose();
      enqueueSnackbar('Questionário respondido com sucesso!', {
        variant: 'success',
      });
    }
  }, [enqueueSnackbar, sendingAnswers, questionsUpdating, handleClose]);

  useEffect(() => {
    setAmmount(Object.keys(questions).length);
    setValues(questions);
  }, [questions]);

  useEffect(() => {
    if (Object.keys(values).length) {
      setQuestionsList(sortArrByKey(convertObjToArr(values), 'order'));
    }
  }, [values]);

  const validateAnswer = useCallback(() => {
    const question = questionsList[activeTab - 1];
    const answerIsValid = !!question.answer.text;
    if (!answerIsValid) {
      setValues(oldValues => ({
        ...oldValues,
        [question.id]: {
          ...oldValues[question.id],
          error: true,
        },
      }));
    }
    return answerIsValid;
  }, [activeTab, questionsList]);

  const handleSlideNext = useCallback(() => {
    if (validateAnswer()) {
      slider.current.slickNext();
    }
  }, [validateAnswer]);

  const handleSlidePrev = useCallback(() => slider.current.slickPrev(), []);

  const handleChange = useCallback(
    question => event => {
      setValues(oldValues => ({
        ...oldValues,
        [question]: {
          ...oldValues[question],
          answer: {
            ...oldValues[question].answer,
            text: event.target.value,
          },
          error: !event.target.value,
        },
      }));
    },
    [],
  );

  const handleSaveQuestions = useCallback(() => {
    if (validateAnswer()) {
      const changedAnswers = convertObjToArr(values).filter(
        value => value.answer.text !== questions[value.id].answer.text,
      );
      if (changedAnswers.length) {
        dispatch(
          ActionsCourse.setLessonAnswers(courseId, lessonId, changedAnswers),
        );
        setSendingAnswers(true);
      } else {
        handleClose();
      }
    }
  }, [
    courseId,
    dispatch,
    handleClose,
    lessonId,
    questions,
    validateAnswer,
    values,
  ]);

  return (
    <Dialog
      className={classes.container}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <Box className="dialog-title-container">
        <Typography className="dialog-title" variant="subtitle1">
          Questionário
        </Typography>
        <IconButton onClick={handleClose}>
          <Icon>close</Icon>
        </IconButton>
      </Box>
      <DialogContent className="dialog-content" dividers>
        {questionsList.length ? (
          <Slider {...settings} ref={slider}>
            {questionsList.map(question => (
              <Box className="dialog-question-container" key={question.id}>
                <Typography className="dialog-question-title" variant="body2">
                  {question.text}
                </Typography>
                <TextField
                  className="dialog-question-input"
                  id="response-input"
                  placeholder="Escreva sua resposta..."
                  multiline
                  rows={4}
                  rowsMax={8}
                  variant="outlined"
                  InputProps={{ classes: { root: classes.input } }}
                  value={values[question.id].answer.text}
                  error={values[question.id].error}
                  helperText={
                    values[question.id].error ? 'Este campo é obrigatório' : ''
                  }
                  onChange={handleChange(question.id)}
                />
              </Box>
            ))}
          </Slider>
        ) : null}
      </DialogContent>
      <DialogActions className="dialog-actions-container">
        <Typography className="dialog-actions-pagination" variant="overline">
          {`${activeTab} / ${ammount}`}
        </Typography>
        <Box>
          {activeTab > 1 ? (
            <Button
              className="dialog-actions-btn-prev"
              color="secondary"
              onClick={handleSlidePrev}
            >
              Anterior
            </Button>
          ) : null}
          {activeTab === ammount ? (
            <Button
              className="dialog-actions-btn-save"
              color="primary"
              variant="contained"
              onClick={handleSaveQuestions}
            >
              {sendingAnswers ? (
                <CircularProgress
                  className="dialog-actions-btn-save-loading"
                  color="inherit"
                  size={24}
                />
              ) : (
                'Salvar'
              )}
            </Button>
          ) : (
            <Button
              className="dialog-actions-btn-next"
              color="primary"
              onClick={handleSlideNext}
            >
              Próxima
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WizardQuestions;
